import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import { ApiResponse, VehicleData, FamilyPlanVehicle } from '../shared/types';
import { LoggerService } from './logger.service';
import { tap } from 'rxjs/operators';
import { getAuthHeadersFromUserData } from '../shared/utils/auth-headers.util';
import type { SubscriptionPause } from '../shared/types/subscription-pause.types';
import type { VehicleActiveMembership } from '../shared/utils/active-membership-plate-warning.util';

export interface VehicleRequestPayload {
  license_plate: string;
  state: string;
  /**
   * Set false by the Add Vehicle forms so the lookup only reads (CWS-1398). The vehicle
   * is then written once, by Save, with the customer on it. Left unset by the camera
   * flows, which rely on the lookup storing what it finds.
   */
  persist?: boolean;
}

export interface CreateVehiclePayload {
  license_plate: string;
  state: string;
  vin: string;
  make: string;
  model: string;
  year: number;
  customer_id: string;
  is_family_plan?: boolean;
  /** Prior owner when reassigning a plate-linked vehicle to a new customer (prepaid redeem). */
  existing_customer_id?: string;
  /** CWS-2053: the attendant confirmed moving this car (id from VEHICLE_TRANSFER_CONFIRM). */
  transfer_vehicle_id?: string;
}

export interface UpdateVehiclePayload {
  license_plate: string;
  state: string;
  vin: string;
  make: string;
  model: string;
  year: number;
  /** Link an existing vehicle record to the customer (after plate lookup / decode). */
  customer_id?: string;
  is_family_plan?: boolean;
  /** CWS-2053: the attendant confirmed taking the plate from this car (VEHICLE_TRANSFER_CONFIRM). */
  transfer_vehicle_id?: string;
}

export interface MembershipSubscription {
  id: string;
  type: 'subscription';
  status: string;
  package_id: string;
  package_name: string;
  wash_type: string;
  price: number;
  source: string;
  stripe_subscription_id?: string;
  current_period_start?: string;
  current_period_end?: string;
  cancel_at_period_end: boolean;
  created_at: string;
  order_id?: string;
  order_number?: string;
  /** Set only for Way pass memberships; those skip the monthly cap like LPR. */
  way_pass_id?: string | null;
  /** Package wash limit; null/omit = unlimited. */
  washes_included?: number | null;
  /** Live count from subscription_washes. */
  washes_redeemed?: number | null;
  last_washed_date?: string | null;
  /** Pause block (CWS-1544); null/omitted when the pause feature is off. */
  pause?: SubscriptionPause | null;
  /** False while paused: listed, but not redeemable. */
  redeemable?: boolean;
}

export interface MembershipPrepaid {
  id: string;
  type: 'prepaid';
  book_id: string;
  book_name: string;
  wash_type: string;
  price: number;
  discount_price?: number;
  redemption_token: string;
  total_items: number;
  redeemed_count: number;
  remaining_washes: number;
  created_at: string;
  order_id?: string;
  order_number?: string;
}

export interface MembershipSingleWash {
  id: string;
  type: 'single_wash';
  package_id: string;
  package_name: string;
  wash_type: string;
  price: number;
  source?: string;
  way_pass_id?: string | null;
  created_at: string;
  appointment_time?: string | null;
  order_id: string;
  order_number?: string;
}

export type Membership = MembershipSubscription | MembershipPrepaid | MembershipSingleWash;

export interface VehicleWithMemberships {
  vehicle: {
    vehicle_id: string;
    license_plate: string;
    state?: string;
    make?: string;
    model?: string;
    year?: number;
    vin?: string;
    default_vehicle: boolean;
    customer_id?: string;
  };
  memberships: Membership[];
}

export interface VehiclesWithMembershipsResponse {
  customer: {
    id: string;
    first_name: string;
    last_name?: string;
    email?: string;
    phone: string;
  };
  vehicles: VehicleWithMemberships[];
}

/**
 * Service for interacting with vehicle-related backend endpoints (CRUD operations, lookups, and family-plan queries).
 * @component
 * @selector app-vehicle-service
 * @example
 *   // Inject the service into a component or another service:
 *   // constructor(private vehicleService: VehicleService) {}
 *   // this.vehicleService.getVehicleById('vehicleId').subscribe(response => { ... });
 * @input string baseUrl - Optional override for the API base URL used for requests (defaults to environment.baseUrl).
 * @output Observable<ApiResponse<any>> apiResponse - All public methods return Observables of ApiResponse<T>; subscribe to these to receive HTTP responses and status information.
 *
 * Key methods:
 * - getVehicleData(payload: VehicleRequestPayload): Observable<ApiResponse<VehicleData>>
 *   Fetch vehicle data by license plate and state. Performs a GET to /vehicle/get-vehicle-data/{state}/{license_plate}.
 *
 * - createVehicle(payload: CreateVehiclePayload): Observable<ApiResponse<any>>
 *   Create a new vehicle record. Performs a POST to /vehicle.
 *
 * - getFamilyPlanVehicles(customerId: string): Observable<ApiResponse<FamilyPlanVehicle[]>>
 *   Retrieve family plan vehicles for a given customer. Performs a GET to /vehicle/get-family-plan-vehicles/{customer_id}.
 *
 * - getVehiclesByCustomerId(customerId: string): Observable<ApiResponse<any[]>>
 *   List vehicles associated with a customer. Performs a GET to /vehicle/list/{customer_id}.
 *
 * - getVehicleById(vehicleId: string): Observable<ApiResponse<any>>
 *   Get details of a single vehicle by its ID. Performs a GET to /vehicle/{vehicle_id}.
 *
 * - updateVehicle(vehicleId: string, payload: UpdateVehiclePayload): Observable<ApiResponse<any>>
 *   Update an existing vehicle by ID. Performs a PUT to /vehicle/{vehicle_id}.
 *
 * - deleteVehicle(vehicleId: string): Observable<ApiResponse<any>>
 *   Delete a vehicle by ID. Performs a DELETE to /vehicle/{vehicle_id}.
 *
 * Important internal helpers:
 * - getHeaders(): HttpHeaders
 *   Constructs authenticated HTTP headers (including JSON content type) for each request using stored user data.
 *
 * Behavior and side effects:
 * - All public methods log API call attempts and outcomes via LoggerService (method, endpoint and inferred status).
 * - Methods return RxJS Observables so consumers can compose, transform, and subscribe as needed.
 *
 * Lifecycle:
 * - Injectable providedIn: 'root' — this is a singleton service instantiated on first injection; no Angular lifecycle hooks (ngOnInit) apply.
 */
@Injectable({
  providedIn: 'root',
})
export class VehicleService {
  private baseUrl = `${environment.baseUrl}/${environment.apiVersion}`;

  constructor(
    private http: HttpClient,
    private logger: LoggerService
  ) {}

  private getHeaders(): HttpHeaders {
    return getAuthHeadersFromUserData({ includeJsonContentType: true });
  }

  /**
   * Fetch vehicle data using license plate and state
   * This is a GET request with path parameters
   */
  getVehicleData(payload: VehicleRequestPayload): Observable<ApiResponse<VehicleData>> {
    this.logger.logApiCall('GET', 'vehicle/plate');

    const url = `${this.baseUrl}/vehicle/plate/${payload.state}/${payload.license_plate}`;
    const params = payload.persist === false ? new HttpParams().set('persist', 'false') : undefined;

    return this.http
      .get<ApiResponse<VehicleData>>(url, {
        headers: this.getHeaders(),
        params,
      })
      .pipe(
        tap(response => this.logger.logApiCall('GET', 'vehicle/plate', response.status ? 200 : 400))
      );
  }

  /**
   * Create a new vehicle
   * This is a POST request to /vehicle endpoint
   */
  createVehicle(payload: CreateVehiclePayload): Observable<ApiResponse<any>> {
    this.logger.logApiCall('POST', 'vehicle');

    const url = `${this.baseUrl}/vehicle`;

    return this.http
      .post<ApiResponse<any>>(url, payload, {
        headers: this.getHeaders(),
      })
      .pipe(
        tap(response => this.logger.logApiCall('POST', 'vehicle', response.status ? 200 : 400))
      );
  }

  /**
   * Get family plan vehicles for a customer
   * GET /vehicle/get-family-plan-vehicles/{customer_id}
   */
  getFamilyPlanVehicles(customerId: string): Observable<ApiResponse<FamilyPlanVehicle[]>> {
    this.logger.logApiCall('GET', `vehicle/customer/${customerId}/family-plan`);

    const url = `${this.baseUrl}/vehicle/customer/${customerId}/family-plan`;

    return this.http
      .get<ApiResponse<FamilyPlanVehicle[]>>(url, {
        headers: this.getHeaders(),
      })
      .pipe(
        tap(response =>
          this.logger.logApiCall(
            'GET',
            `vehicle/customer/${customerId}/family-plan`,
            response.status ? 200 : 400
          )
        )
      );
  }

  /**
   * Get vehicles list by customer ID
   * This is a GET request to /vehicle/list/{customer_id} endpoint
   */
  getVehiclesByCustomerId(customerId: string): Observable<ApiResponse<any[]>> {
    this.logger.logApiCall('GET', `vehicle/customer/${customerId}`);

    const url = `${this.baseUrl}/vehicle/customer/${customerId}`;

    return this.http
      .get<ApiResponse<any[]>>(url, {
        headers: this.getHeaders(),
      })
      .pipe(
        tap(response =>
          this.logger.logApiCall(
            'GET',
            `vehicle/customer/${customerId}`,
            response.status ? 200 : 400
          )
        )
      );
  }

  /**
   * Get vehicle details by vehicle ID
   * This is a GET request to /vehicle/{vehicle_id} endpoint
   */
  getVehicleById(vehicleId: string): Observable<ApiResponse<any>> {
    this.logger.logApiCall('GET', `vehicle/${vehicleId}`);

    const url = `${this.baseUrl}/vehicle/${vehicleId}`;

    return this.http
      .get<ApiResponse<any>>(url, {
        headers: this.getHeaders(),
      })
      .pipe(
        tap(response =>
          this.logger.logApiCall('GET', `vehicle/${vehicleId}`, response.status ? 200 : 400)
        )
      );
  }

  /**
   * Update a vehicle by ID
   * This is a PUT request to /vehicle/{vehicle_id} endpoint
   */
  updateVehicle(vehicleId: string, payload: UpdateVehiclePayload): Observable<ApiResponse<any>> {
    this.logger.logApiCall('PUT', `vehicle/${vehicleId}`);

    const url = `${this.baseUrl}/vehicle/${vehicleId}`;

    return this.http
      .put<ApiResponse<any>>(url, payload, {
        headers: this.getHeaders(),
      })
      .pipe(
        tap(response =>
          this.logger.logApiCall('PUT', `vehicle/${vehicleId}`, response.status ? 200 : 400)
        )
      );
  }

  /**
   * Active memberships covering a vehicle (CWS-1384).
   *
   * GET /vehicle/{vehicle_id}/active-memberships. Answers from the same predicate the
   * delete guard refuses on, so the plate-edit warning and the delete block can never
   * disagree about what counts as an active membership.
   */
  getActiveMemberships(
    vehicleId: string
  ): Observable<
    ApiResponse<{ memberships: VehicleActiveMembership[]; has_active_membership: boolean }>
  > {
    this.logger.logApiCall('GET', `vehicle/${vehicleId}/active-memberships`);

    const url = `${this.baseUrl}/vehicle/${vehicleId}/active-memberships`;

    return this.http
      .get<ApiResponse<{ memberships: VehicleActiveMembership[]; has_active_membership: boolean }>>(
        url,
        { headers: this.getHeaders() }
      )
      .pipe(
        tap(response =>
          this.logger.logApiCall(
            'GET',
            `vehicle/${vehicleId}/active-memberships`,
            response.status ? 200 : 400
          )
        )
      );
  }

  /**
   * Delete a vehicle by ID
   * This is a DELETE request to /vehicle/{vehicle_id} endpoint
   */
  deleteVehicle(vehicleId: string): Observable<ApiResponse<any>> {
    this.logger.logApiCall('DELETE', `vehicle/${vehicleId}`);

    const url = `${this.baseUrl}/vehicle/${vehicleId}`;

    return this.http
      .delete<ApiResponse<any>>(url, {
        headers: this.getHeaders(),
      })
      .pipe(
        tap(response =>
          this.logger.logApiCall('DELETE', `vehicle/${vehicleId}`, response.status ? 200 : 400)
        )
      );
  }

  /**
   * Get all vehicles with active memberships for a customer by phone number.
   * Used by attendant UI to show all license plates and their memberships when entering phone.
   * This is a GET request to /vehicle/memberships/phone/{phone} endpoint
   *
   * @param phone Customer phone number
   * @returns Observable with customer info and array of vehicles with their memberships
   */
  getVehiclesWithMembershipsByPhone(
    phone: string
  ): Observable<ApiResponse<VehiclesWithMembershipsResponse>> {
    const encodedPhone = encodeURIComponent(phone);
    this.logger.logApiCall('GET', `vehicle/memberships/phone/${encodedPhone}`);

    const url = `${this.baseUrl}/vehicle/memberships/phone/${encodedPhone}`;

    return this.http
      .get<ApiResponse<VehiclesWithMembershipsResponse>>(url, {
        headers: this.getHeaders(),
      })
      .pipe(
        tap(response =>
          this.logger.logApiCall(
            'GET',
            `vehicle/memberships/phone/${encodedPhone}`,
            response.status ? 200 : 400
          )
        )
      );
  }
}
